/** Mesmo recorte de URL do chat interno (`TeamLinkifiedText`). */
const URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"{}|\\^`[\]]+/gi;
const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;

function trimUrlTrailingPunctuation(url) {
  let trimmed = url;
  let suffix = "";

  while (/[.,!?;:]$/.test(trimmed)) {
    suffix = trimmed.slice(-1) + suffix;
    trimmed = trimmed.slice(0, -1);
  }

  while (/\)$/.test(trimmed)) {
    const openCount = (trimmed.match(/\(/g) || []).length;
    const closeCount = (trimmed.match(/\)/g) || []).length;
    if (closeCount <= openCount) break;
    suffix = ")" + suffix;
    trimmed = trimmed.slice(0, -1);
  }

  while (/\]$/.test(trimmed)) {
    const openCount = (trimmed.match(/\[/g) || []).length;
    const closeCount = (trimmed.match(/\]/g) || []).length;
    if (closeCount <= openCount) break;
    suffix = "]" + suffix;
    trimmed = trimmed.slice(0, -1);
  }

  return { url: trimmed, suffix };
}

function hrefForUrl(url) {
  return /^www\./i.test(url) ? `https://${url}` : url;
}

function isYouTubeHost(hostname) {
  const host = String(hostname || "").toLowerCase().replace(/\.$/, "");
  return (
    host === "youtu.be" ||
    host === "youtube.com" ||
    host.endsWith(".youtube.com") ||
    host === "youtube-nocookie.com" ||
    host.endsWith(".youtube-nocookie.com")
  );
}

function parseYouTubeDuration(raw) {
  const v = String(raw || "").trim();
  if (!v) return 0;
  if (/^\d+$/.test(v)) {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }
  const m = v.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i);
  if (!m || (!m[1] && !m[2] && !m[3])) return 0;
  return Number(m[1] || 0) * 3600 + Number(m[2] || 0) * 60 + Number(m[3] || 0);
}

function startSecondsFromUrl(url) {
  const candidates = [
    url.searchParams.get("t"),
    url.searchParams.get("start"),
    url.searchParams.get("time_continue"),
  ];
  const hashT = String(url.hash || "").match(/[?&#]?t=([^&]+)/i);
  if (hashT) candidates.push(hashT[1]);

  for (const raw of candidates) {
    const seconds = parseYouTubeDuration(raw);
    if (seconds > 0) return seconds;
  }
  return 0;
}

function videoIdFromPath(pathname, searchParams) {
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean);
  const fromQuery = searchParams.get("v");
  if (fromQuery) return fromQuery;

  const [head, id] = parts;
  if (!head) return null;
  if (head === "embed" || head === "shorts" || head === "live" || head === "v" || head === "e") {
    return id || null;
  }
  return null;
}

/**
 * Interpreta um URL solto (http(s) ou www.). Retorna null se não for vídeo do YouTube.
 */
export function parseYouTubeUrl(raw) {
  const href = hrefForUrl(String(raw || "").trim());
  if (!href) return null;

  let url;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  if (!isYouTubeHost(url.hostname)) return null;

  const host = url.hostname.toLowerCase().replace(/^www\./, "");
  let videoId = null;
  let isShorts = false;

  if (host === "youtu.be") {
    videoId = url.pathname.split("/").filter(Boolean)[0] || null;
  } else {
    const parts = url.pathname.split("/").filter(Boolean);
    isShorts = parts[0] === "shorts";
    videoId = videoIdFromPath(url.pathname, url.searchParams);
  }

  if (!VIDEO_ID_RE.test(videoId || "")) return null;

  return {
    videoId,
    startSeconds: startSecondsFromUrl(url),
    isShorts,
    sourceUrl: url.toString(),
  };
}

export function extractYouTubeVideosFromText(text) {
  const str = String(text || "");
  if (!str) return [];

  const seen = new Set();
  const videos = [];
  const re = new RegExp(URL_PATTERN.source, "gi");
  let match;

  while ((match = re.exec(str)) !== null) {
    const { url } = trimUrlTrailingPunctuation(match[0]);
    const parsed = parseYouTubeUrl(url);
    if (!parsed || seen.has(parsed.videoId)) continue;
    seen.add(parsed.videoId);
    videos.push(parsed);
  }

  return videos;
}

/** True quando o texto, ignorando espaços, é só um ou mais links de vídeo do YouTube. */
export function isYouTubeOnlyText(text) {
  const raw = String(text || "").trim();
  if (!raw) return false;
  const videos = extractYouTubeVideosFromText(raw);
  if (!videos.length) return false;

  const stripped = raw
    .replace(new RegExp(URL_PATTERN.source, "gi"), (full) => {
      const { url } = trimUrlTrailingPunctuation(full);
      return parseYouTubeUrl(url) ? " " : full;
    })
    .replace(/\s+/g, "")
    .trim();

  return stripped === "";
}

export function youtubeThumbnailUrl(videoId) {
  if (!VIDEO_ID_RE.test(String(videoId || ""))) return "";
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

export function youtubeEmbedSrc({ videoId, startSeconds = 0, autoplay = false } = {}) {
  if (!VIDEO_ID_RE.test(String(videoId || ""))) return "";
  const params = new URLSearchParams({
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
  });
  if (autoplay) params.set("autoplay", "1");
  if (Number(startSeconds) > 0) params.set("start", String(Math.floor(Number(startSeconds))));
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
}
